import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { AppHeader } from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Loader2, CalendarDays, Share2, Link2, Check } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/calendar")({
  head: () => ({
    meta: [
      { title: "Calendar — Auditions & callbacks at a glance | RoleBoard" },
      {
        name: "description",
        content:
          "See every audition and callback across the month, and share a private iCal feed with your reps or partner.",
      },
      { property: "og:title", content: "Calendar — RoleBoard" },
      {
        property: "og:description",
        content:
          "Auditions and callbacks across the month, with a private iCal feed.",
      },
      { property: "og:url", content: "https://roleboard.app/calendar" },
    ],
    links: [{ rel: "canonical", href: "https://roleboard.app/calendar" }],
  }),
  component: CalendarPage,
});

type Audition = {
  id: string;
  project_name: string;
  role_name: string;
  audition_date: string | null;
  callback_date: string | null;
  outcome: string | null;
  audition_format: string | null;
  company_name: string | null;
};

type DayEvent = {
  audition: Audition;
  kind: "audition" | "callback";
};

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEKDAYS_SHORT = ["S", "M", "T", "W", "T", "F", "S"];
const CALENDAR_BASE_URL = "https://roleboard.app";

function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function CalendarPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !user) navigate({ to: "/login" });
  }, [user, authLoading, navigate]);

  const [auditions, setAuditions] = useState<Audition[]>([]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState<Date>(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [syncOpen, setSyncOpen] = useState(false);

  useEffect(() => {
    async function load() {
      if (!user) return;
      setLoading(true);
      const { data, error } = await supabase
        .from("auditions")
        .select(
          "id, project_name, role_name, audition_date, callback_date, outcome, audition_format, company_name",
        )
        .order("audition_date", { ascending: true });
      if (error) toast.error("Couldn't load your calendar. Please try again.");
      else setAuditions((data as Audition[]) ?? []);
      setLoading(false);
    }
    if (user) load();
  }, [user]);

  // Group events (auditions + callbacks) by date string
  const byDate = useMemo(() => {
    const map = new Map<string, DayEvent[]>();
    const push = (key: string, ev: DayEvent) => {
      const arr = map.get(key) ?? [];
      arr.push(ev);
      map.set(key, arr);
    };
    for (const a of auditions) {
      if (a.audition_date) push(a.audition_date.slice(0, 10), { audition: a, kind: "audition" });
      if (a.callback_date) push(a.callback_date.slice(0, 10), { audition: a, kind: "callback" });
    }
    return map;
  }, [auditions]);

  // Build month grid (always 6 rows × 7 columns for stable layout)
  const grid = useMemo(() => {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const firstDay = new Date(year, month, 1);
    const startWeekday = firstDay.getDay();
    const startDate = new Date(year, month, 1 - startWeekday);
    const cells: { date: Date; inMonth: boolean; key: string }[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(startDate);
      d.setDate(startDate.getDate() + i);
      cells.push({
        date: d,
        inMonth: d.getMonth() === month,
        key: ymd(d),
      });
    }
    return cells;
  }, [cursor]);

  const monthLabel = cursor.toLocaleString("default", {
    month: "long",
    year: "numeric",
  });

  const todayKey = ymd(new Date());
  const selectedAuditions = selectedDate ? byDate.get(selectedDate) ?? [] : [];

  // Upcoming events list (next 30 days, both auditions and callbacks)
  const upcoming = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const horizon = new Date(today);
    horizon.setDate(horizon.getDate() + 60);

    const events: Array<DayEvent & { date: Date }> = [];
    for (const a of auditions) {
      if (a.audition_date) {
        const d = new Date(a.audition_date + "T00:00:00");
        if (d >= today && d <= horizon) {
          events.push({ audition: a, kind: "audition", date: d });
        }
      }
      if (a.callback_date) {
        const d = new Date(a.callback_date + "T00:00:00");
        if (d >= today && d <= horizon) {
          events.push({ audition: a, kind: "callback", date: d });
        }
      }
    }
    return events.sort((a, b) => a.date.getTime() - b.date.getTime()).slice(0, 6);
  }, [auditions]);

  function relativeLabel(d: Date): string {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diff = Math.round((d.getTime() - today.getTime()) / 86400000);
    if (diff === 0) return "Today";
    if (diff === 1) return "Tomorrow";
    if (diff < 7) return `In ${diff} days`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }

  if (authLoading || !user) {
    return (
      <div className="min-h-screen bg-background grid place-items-center">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <AppHeader />
      <main className="container max-w-5xl mx-auto px-5 md:px-8 py-8 md:py-12">
        <div className="mb-6 md:mb-8 flex items-end justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">
              Calendar
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Visualize your auditions over time.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSyncOpen(true)}
            className="shrink-0 text-muted-foreground hover:text-foreground"
          >
            <Share2 className="size-3.5 mr-1.5" />
            <span className="hidden sm:inline">Sync with Calendar</span>
            <span className="sm:hidden">Sync</span>
          </Button>
        </div>

        <CalendarSyncDialog open={syncOpen} onOpenChange={setSyncOpen} />

        {/* Upcoming panel */}
        {!loading && upcoming.length > 0 && (
          <Card className="p-4 md:p-5 mb-5 animate-fade-in">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                Upcoming
              </h2>
              <span className="text-[11px] text-muted-foreground">
                Next {upcoming.length} event{upcoming.length === 1 ? "" : "s"}
              </span>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {upcoming.map((ev, idx) => {
                const isCallback = ev.kind === "callback";
                return (
                  <button
                    key={`${ev.audition.id}-${ev.kind}-${idx}`}
                    type="button"
                    onClick={() => setSelectedDate(ymd(ev.date))}
                    className={cn(
                      "group flex items-center gap-3 rounded-md border px-3 py-2 text-left transition-colors hover:bg-muted/40",
                      isCallback
                        ? "border-blue-500/30 bg-blue-500/5"
                        : "border-border bg-background/40",
                    )}
                  >
                    <span
                      className={cn(
                        "size-1.5 rounded-full shrink-0",
                        isCallback ? "bg-blue-500" : "bg-primary/70",
                      )}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {ev.audition.project_name}
                        </p>
                        {isCallback && (
                          <span className="text-[9px] font-medium px-1.5 py-px rounded bg-blue-500/15 text-blue-600 dark:text-blue-400 shrink-0">
                            Callback
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {ev.audition.role_name}
                      </p>
                    </div>
                    <span className="text-[11px] text-muted-foreground shrink-0">
                      {relativeLabel(ev.date)}
                    </span>
                  </button>
                );
              })}
            </div>
          </Card>
        )}

        <Card className="p-4 md:p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() =>
                  setCursor(
                    new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1),
                  )
                }
                aria-label="Previous month"
              >
                <ChevronLeft className="size-4" />
              </Button>
              <h2 className="text-base md:text-lg font-semibold tracking-tight min-w-[160px] text-center">
                {monthLabel}
              </h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() =>
                  setCursor(
                    new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1),
                  )
                }
                aria-label="Next month"
              >
                <ChevronRight className="size-4" />
              </Button>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const d = new Date();
                setCursor(new Date(d.getFullYear(), d.getMonth(), 1));
              }}
            >
              Today
            </Button>
          </div>

          {/* Weekday header */}
          <div className="grid grid-cols-7 mb-1">
            {WEEKDAYS.map((d, i) => (
              <div
                key={d}
                className="text-[10px] md:text-[11px] font-medium text-muted-foreground text-center py-1"
              >
                <span className="hidden md:inline">{d}</span>
                <span className="md:hidden">{WEEKDAYS_SHORT[i]}</span>
              </div>
            ))}
          </div>

          {/* Grid */}
          {loading ? (
            <div className="grid place-items-center py-16">
              <Loader2 className="size-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="grid grid-cols-7 gap-1">
              {grid.map((cell) => {
                const items = byDate.get(cell.key) ?? [];
                const count = items.length;
                const isToday = cell.key === todayKey;
                const isFuture =
                  new Date(cell.key + "T00:00:00") >=
                  new Date(todayKey + "T00:00:00");
                return (
                  <button
                    key={cell.key}
                    type="button"
                    onClick={() => setSelectedDate(cell.key)}
                    disabled={!cell.inMonth && count === 0}
                    className={cn(
                      "group relative h-16 md:h-20 rounded-md border text-left p-1 md:p-1.5 transition-colors flex flex-col gap-1",
                      "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      cell.inMonth
                        ? "bg-background hover:bg-muted/60 border-border/70"
                        : "bg-muted/10 border-transparent text-muted-foreground/50",
                      isToday && "border-primary/70",
                      count > 0 && cell.inMonth && "hover:border-primary/40",
                    )}
                  >
                    <div className="flex items-start justify-between leading-none">
                      <span
                        className={cn(
                          "text-[11px] md:text-xs font-medium",
                          isToday &&
                            "inline-flex items-center justify-center size-5 rounded-full bg-primary text-primary-foreground",
                        )}
                      >
                        {cell.date.getDate()}
                      </span>
                    </div>

                    {/* Event pills */}
                    {count > 0 && (
                      <div className="flex flex-col gap-0.5 overflow-hidden">
                        {items.slice(0, 2).map((ev, idx) => {
                          const a = ev.audition;
                          const isCallback = ev.kind === "callback";
                          const cls = isCallback
                            ? "bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30"
                            : a.outcome === "Booked"
                              ? "bg-green-500/15 text-green-700 dark:text-green-300 border-green-500/30"
                              : isFuture
                                ? "bg-muted/60 text-muted-foreground border-border"
                                : "bg-primary/10 text-primary border-primary/25";
                          const label = isCallback
                            ? "Callback"
                            : a.outcome === "Booked"
                              ? "Booked"
                              : isFuture
                                ? "Upcoming"
                                : "Audition";
                          return (
                            <span
                              key={`${a.id}-${ev.kind}-${idx}`}
                              className={cn(
                                "truncate text-[9px] md:text-[10px] leading-none px-1 md:px-1.5 py-0.5 rounded border font-medium",
                                cls,
                              )}
                              title={`${a.project_name} · ${label}`}
                            >
                              <span className="hidden md:inline">{a.project_name}</span>
                              <span className="md:hidden">{label}</span>
                            </span>
                          );
                        })}
                        {count > 2 && (
                          <span className="text-[9px] leading-none text-muted-foreground pl-1">
                            +{count - 2} more
                          </span>
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Legend */}
          <div className="mt-4 pt-3 border-t border-border/60 flex flex-wrap items-center gap-x-3 gap-y-2 text-[11px]">
            <LegendPill className="bg-muted/60 text-muted-foreground border-border">
              Upcoming
            </LegendPill>
            <LegendPill className="bg-primary/10 text-primary border-primary/25">
              Audition
            </LegendPill>
            <LegendPill className="bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30">
              Callback
            </LegendPill>
            <LegendPill className="bg-green-500/15 text-green-700 dark:text-green-300 border-green-500/30">
              Booked
            </LegendPill>
          </div>
        </Card>
      </main>

      <Dialog
        open={!!selectedDate}
        onOpenChange={(open) => !open && setSelectedDate(null)}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="size-4 text-muted-foreground" />
              {selectedDate
                ? new Date(selectedDate + "T00:00:00").toLocaleDateString(
                    undefined,
                    {
                      weekday: "long",
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    },
                  )
                : ""}
            </DialogTitle>
            <DialogDescription>
              {selectedAuditions.length === 0
                ? "No auditions on this day."
                : `${selectedAuditions.length} audition${selectedAuditions.length === 1 ? "" : "s"}`}
            </DialogDescription>
          </DialogHeader>

          {selectedAuditions.length > 0 && (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto -mx-2 px-2">
              {selectedAuditions.map((ev, idx) => {
                const a = ev.audition;
                const isCallback = ev.kind === "callback";
                return (
                <div
                  key={`${a.id}-${ev.kind}-${idx}`}
                  className={cn(
                    "rounded-md border p-3 hover:bg-muted/40 transition-colors",
                    isCallback ? "border-blue-500/40 bg-blue-500/5" : "border-border",
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium text-sm tracking-tight truncate flex items-center gap-2">
                        {a.project_name}
                        {isCallback && (
                          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/30">
                            Callback
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {a.role_name}
                        {a.company_name ? ` · ${a.company_name}` : ""}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      {!isCallback && a.outcome && a.outcome !== "Not Yet Known" && (
                        <Badge
                          variant={
                            a.outcome === "Booked"
                              ? "default"
                              : a.outcome === "Callback"
                                ? "secondary"
                                : "outline"
                          }
                          className="text-[10px]"
                        >
                          {a.outcome}
                        </Badge>
                      )}
                      {a.audition_format && (
                        <span className="text-[10px] text-muted-foreground">
                          {a.audition_format}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSelectedDate(null);
                navigate({ to: "/auditions", search: {} as never });
              }}
            >
              Go to Auditions
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LegendPill({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center px-2 py-0.5 rounded-full border font-medium",
        className,
      )}
    >
      {children}
    </span>
  );
}

function CalendarSyncDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { user } = useAuth();
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) setCopied(false);
  }, [open]);

  useEffect(() => {
    if (!open || token || !user) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data: existing, error: readErr } = await supabase
          .from("calendar_feed_tokens")
          .select("token")
          .eq("user_id", user.id)
          .maybeSingle();
        if (readErr) throw readErr;
        if (existing?.token) {
          if (!cancelled) setToken(existing.token);
          return;
        }
        const arr = new Uint8Array(24);
        crypto.getRandomValues(arr);
        const newToken = Array.from(arr, (b) => b.toString(16).padStart(2, "0")).join("");
        const { error: insertErr } = await supabase
          .from("calendar_feed_tokens")
          .insert({ user_id: user.id, token: newToken });
        if (insertErr) throw insertErr;
        if (!cancelled) setToken(newToken);
      } catch (e: unknown) {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : "Could not generate link";
          toast.error(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, token, user]);

  const httpsUrl = token ? `${CALENDAR_BASE_URL}/api/public/calendar/${token}/calendar.ics` : "";

  async function copyLink() {
    if (!httpsUrl) return;
    try {
      await navigator.clipboard.writeText(httpsUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy link");
    }
  }

  const displayUrl = loading
    ? "Preparing your secure link…"
    : httpsUrl.length > 44
      ? `${httpsUrl.slice(0, 26)}…${httpsUrl.slice(-12)}`
      : httpsUrl;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="mx-auto w-[calc(100vw-1.5rem)] max-w-md overflow-hidden rounded-2xl p-0 sm:w-full">
        <DialogHeader className="space-y-2 px-6 pb-3 pr-14 pt-7 text-left sm:px-8 sm:pr-12">
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <CalendarDays className="size-4 text-muted-foreground" />
            Sync with Calendar
          </DialogTitle>
          <DialogDescription className="max-w-[40ch] text-sm leading-relaxed">
            Subscribe once. Upcoming auditions and callbacks appear in your calendar automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="min-w-0 space-y-7 px-6 pb-7 pt-3 sm:space-y-6 sm:px-8 sm:pb-6">
          <div className="space-y-2.5">
            <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground/80">
              Private calendar feed
            </div>

            {/* Mobile: stacked URL + full-width copy button */}
            <div className="min-w-0 space-y-2 sm:hidden">
              <div className="mx-1 min-w-0 max-w-full overflow-hidden rounded-lg border border-primary/20 bg-primary/[0.04] px-4 py-3.5">
                <span
                  className="block min-w-0 overflow-hidden text-ellipsis whitespace-nowrap font-mono text-[13px] text-foreground/80"
                  title={httpsUrl}
                >
                  {displayUrl}
                </span>
              </div>
              <button
                type="button"
                onClick={copyLink}
                disabled={loading || !httpsUrl}
                className="mx-1 flex h-11 w-[calc(100%-0.5rem)] min-w-0 max-w-full items-center justify-center gap-2 rounded-lg border border-primary/20 bg-primary/[0.06] text-sm font-medium text-primary transition-colors hover:bg-primary/10 active:bg-primary/15 disabled:opacity-50"
                aria-label="Copy calendar link"
              >
                {copied ? (
                  <>
                    <Check className="size-4" />
                    <span>Copied</span>
                  </>
                ) : (
                  <>
                    <Link2 className="size-4" />
                    <span>Copy calendar link</span>
                  </>
                )}
              </button>
            </div>

            {/* Desktop: inline URL + copy */}
            <div className="hidden min-w-0 w-full items-center gap-2 overflow-hidden rounded-lg border border-primary/20 bg-primary/[0.04] px-3 py-2.5 sm:flex">
              <span
                className="min-w-0 flex-1 truncate font-mono text-xs text-foreground/80"
                title={httpsUrl}
              >
                {displayUrl}
              </span>
              <button
                type="button"
                onClick={copyLink}
                disabled={loading || !httpsUrl}
                className="relative flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-xs text-primary/80 transition-colors hover:bg-primary/10 hover:text-primary disabled:opacity-50"
                aria-label="Copy calendar link"
              >
                {copied ? (
                  <>
                    <Check className="size-3.5" />
                    <span>Copied</span>
                  </>
                ) : (
                  <>
                    <Link2 className="size-3.5" />
                    <span>Copy</span>
                  </>
                )}
              </button>
            </div>

            <p
              className={cn(
                "text-[11px] text-primary/80 transition-opacity duration-300",
                copied ? "opacity-100" : "opacity-0",
              )}
              aria-live="polite"
            >
              Calendar link copied
            </p>
          </div>

          {/* Mobile: stacked instructions */}
          <div className="space-y-5 sm:hidden">
            <div className="space-y-1.5">
              <div className="text-[11px] font-medium uppercase tracking-wide text-foreground/70">
                Apple
              </div>
              <p className="text-[13px] leading-relaxed text-muted-foreground">
                Calendar → Add Subscription Calendar
              </p>
            </div>
            <div className="space-y-1.5">
              <div className="text-[11px] font-medium uppercase tracking-wide text-foreground/70">
                Google
              </div>
              <p className="text-[13px] leading-relaxed text-muted-foreground">
                Other calendars → From URL
              </p>
            </div>
          </div>

          {/* Desktop: editorial inline rows */}
          <div className="hidden space-y-3 text-xs leading-relaxed sm:block">
            <div className="flex items-baseline gap-3">
              <span className="w-14 shrink-0 text-[11px] font-medium uppercase tracking-wide text-foreground/70">
                Apple
              </span>
              <span className="text-muted-foreground">
                Calendar → Add Subscription Calendar
              </span>
            </div>
            <div className="flex items-baseline gap-3">
              <span className="w-14 shrink-0 text-[11px] font-medium uppercase tracking-wide text-foreground/70">
                Google
              </span>
              <span className="text-muted-foreground">
                Other calendars → From URL
              </span>
            </div>
          </div>

          <p className="text-[11px] leading-relaxed text-muted-foreground/70">
            Private to your account. Updates automatically.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
